import { CommonModule } from '@angular/common';

import { Component, computed, inject, signal } from '@angular/core';

import { RouterLink, Router } from '@angular/router';

import { FormsModule } from '@angular/forms';

import {
  ShamLeadingIcon,
  ShamNocouponIcon,
  ShamMessengerIcon,
  ShamShare08Icon,
  ShamTelegramIcon,
  ShamWhatsappIcon,
} from 'sham-components/icons';

import { TranslateModule } from '@ngx-translate/core';

import { QuantitySelector } from '@/app/elements/quantity-selector/quantity-selector';

import { CartItemActions } from '@/app/elements/cart-item-actions/cart-item-actions';

import {
  CartApi,
  AvailableCoupon,
  CartItem,
  CartSnapshot,
  SellerGroup,
} from '@/app/services/cart-api';

import {
  ShopVoucherModal,
  CouponType,
} from '@/app/elements/shop-voucher-modal/shop-voucher-modal';
import { EmptyPage } from '@/app/layouts/empty-page/empty-page';
import { AuthService } from '@/app/services/auth.service';
import { ToastService } from '@/app/services/http-error-notification.service';
import { mediaUrl } from '@/app/utils/media-url';

@Component({
  selector: 'app-cart-page',

  standalone: true,

  imports: [
    RouterLink,

    CommonModule,

    FormsModule,

    TranslateModule,

    QuantitySelector,

    CartItemActions,

    ShamLeadingIcon,
    ShamNocouponIcon,

    ShamMessengerIcon,

    ShamTelegramIcon,

    ShamWhatsappIcon,

    ShamShare08Icon,

    ShopVoucherModal,
    EmptyPage,
  ],

  templateUrl: './shopping-cart-page.html',

  styleUrl: './shopping-cart-page.css',
})
export class CartPage {
  isLoading = signal(false);

  isVoucherModalOpen = signal<Record<string, boolean>>({});

  applyingCouponFor = signal<string | null>(null);

  private cartApi = inject(CartApi);

  private router = inject(Router);

  private authService = inject(AuthService);
  private toastService = inject(ToastService);

  allSelected = computed(() => {
    const snapshot = this.cartSnapshot();

    if (!snapshot) return false;

    return snapshot.sellerGroups

      .flatMap((g) => g.items)

      .every((i) => i.isSelectedForCheckout);
  });

  voucher = '';

  selectAll = signal(true);

  cartSnapshot = signal<CartSnapshot | null>(null);

  // Local state for UI only

  selectedItems = signal<Set<string>>(new Set());

  favoriteItems = signal<Set<string>>(new Set());

  readonly mediaUrl = mediaUrl;

  ngOnInit() {
    this.loadCart();

    this.loadWishlist();
  }

  loadWishlist() {
    this.cartApi.getWishlist().subscribe({
      next: (wishlist) => {
        const favorites = wishlist.items.map(
          (item) => `${item.productId}_${item.variantId}`,
        );

        this.favoriteItems.set(new Set(favorites));
      },

      error: (err) => {
        console.error('Error loading wishlist:', err);
      },
    });
  }

  loadCart() {
    this.isLoading.set(true);

    this.cartApi.getCart().subscribe({
      next: (snapshot) => {
        this.syncSelection(snapshot);

        const backendSelectedIds = snapshot.sellerGroups

          .flatMap((g) => g.items)

          .filter((item) => item.isSelectedForCheckout)

          .map((item) => item.itemId);

        this.selectedItems.set(new Set(backendSelectedIds));

        this.isLoading.set(false);
      },

      error: (err) => {
        console.error(err);

        this.isLoading.set(false);
      },
    });
  }

  subtotal = computed(() => {
    const snapshot = this.cartSnapshot();
    const selectedItemIds = this.selectedItems();

    if (!snapshot) return 0;

    return snapshot.sellerGroups
      .flatMap((group) => group.items)
      .filter((item) => selectedItemIds.has(item.itemId))
      .reduce((sum, item) => sum + item.totalPrice, 0);
  });

  total = computed(() => Math.max(this.subtotal() - this.totalDiscount(), 0));

  totalDiscount = computed(() => {
    const snapshot = this.cartSnapshot();
    const selectedItemIds = this.selectedItems();

    if (!snapshot) return 0;

    return snapshot.sellerGroups.reduce((discount, group) => {
      // A seller discount cannot be accurately apportioned across a partial selection.
      const isEntireSellerSelected = group.items.every((item) =>
        selectedItemIds.has(item.itemId),
      );

      return isEntireSellerSelected && group.selectedCoupon?.isAvailable
        ? discount + group.selectedCoupon.sellerDiscountAmount
        : discount;
    }, 0);
  });

  currency = computed(
    () =>
      this.cartSnapshot()?.couponSummary?.currency ??
      this.cartSnapshot()?.subtotalCurrency ??
      '',
  );

  groupedCartItems = computed(() => {
    return this.cartSnapshot()?.sellerGroups ?? [];
  });

  selectedCount = computed(() => this.selectedItems().size);

  toggleSelectAll(event: any) {
    const isChecked = event.target.checked;

    this.selectAll.set(isChecked);

    if (isChecked) {
      const allIds =
        this.cartSnapshot()?.sellerGroups.flatMap((g) =>
          g.items.map((i) => i.itemId),
        ) ?? [];

      this.cartApi.setCheckoutSelection(allIds).subscribe({
        next: (res) => this.syncSelection(res),

        error: (err) => console.error(err),
      });
    } else {
      this.selectedItems.set(new Set());
    }
  }

  isItemSelected(itemId: string) {
    return this.selectedItems().has(itemId);
  }

  toggleSelectItem(itemId: string) {
    const isSelected = !this.selectedItems().has(itemId);

    this.selectedItems.update((set) => {
      const copy = new Set(set);

      isSelected ? copy.add(itemId) : copy.delete(itemId);

      return copy;
    });

    // backend

    this.cartApi.setItemCheckoutSelection(itemId, isSelected).subscribe({
      next: (res) => this.syncSelection(res),

      error: () => {
        // rollback

        this.toggleSelectItem(itemId);
      },
    });
  }

  updateQty(itemId: string, currentQty: number, change: number) {
    const newQty = currentQty + change;

    if (newQty <= 0) {
      this.removeItem(itemId);
    } else {
      this.cartApi.updateQuantity(itemId, newQty).subscribe({
        next: (snapshot) => this.syncSelection(snapshot),

        error: (err) => console.error('Error updating quantity:', err),
      });
    }
  }

  removeItem(itemId: string) {
    this.cartApi.deleteItem(itemId).subscribe({
      next: (snapshot) => {
        this.syncSelection(snapshot);

        this.selectedItems.update((set) => {
          const newSet = new Set(set);

          newSet.delete(itemId);

          return newSet;
        });
      },

      error: (err) => console.error('Error removing item:', err),
    });
  }

  toggleFav(item: CartItem) {
    const key = `${item.productId}_${item.variantId}`;

    if (this.favoriteItems().has(key)) {
      this.cartApi.removeFromWishlist(item.productId, item.variantId).subscribe({
        next: () => {
          this.favoriteItems.update((set) => {
            const copy = new Set(set);

            copy.delete(key);

            return copy;
          });
          this.showToast('Removed from favorites', 'success');
        },

        error: () => this.showToast('Unable to remove item from favorites', 'error'),
      });
    } else {
      this.cartApi.addToWishlist(item.productId, item.variantId).subscribe({
        next: () => {
          this.favoriteItems.update((set) => {
            const copy = new Set(set);

            copy.add(key);

            return copy;
          });
          this.showToast('Added to favorites', 'success');
        },

        error: () => this.showToast('Unable to add item to favorites', 'error'),
      });
    }
  }

  private showToast(message: string, type: 'success' | 'error') {
    this.toastService.show(message, type);
  }

  isFavorite(productId: string, variantId: string) {
    return this.favoriteItems().has(`${productId}_${variantId}`);
  }

  deleteSelected() {
    const ids = Array.from(this.selectedItems());

    if (ids.length === 0) return;

    this.cartApi.bulkDelete(ids).subscribe({
      next: (snapshot) => {
        this.syncSelection(snapshot);

        this.selectedItems.set(new Set());
      },

      error: (err) => console.error('Error bulk deleting:', err),
    });
  }

  applyVoucher() {
    console.log('Applying voucher:', this.voucher);
  }

  checkout() {
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: '/cart' },
      });
      return;
    }

    this.router.navigate(['/checkout']);
  }

  shareCart() {
    console.log('Sharing cart...');
  }

  goBack() {
    this.router.navigate(['/home']);
  }

  getOptionsText(options: any[]): string {
    if (!options || options.length === 0) return '';

    return options

      .map((opt) => `${opt.attributeName}: ${opt.attributeValueName}`)

      .join(', ');
  }

  toggleGlobalSelectAll(event: any) {
    const isChecked = event.target.checked;

    const allIds =
      this.cartSnapshot()?.sellerGroups.flatMap((g) =>
        g.items.map((i) => i.itemId),
      ) ?? [];

    this.selectedItems.set(isChecked ? new Set(allIds) : new Set());

    this.cartApi.setCheckoutSelection(isChecked ? allIds : []).subscribe({
      next: (res) => this.syncSelection(res),

      error: (err) => console.error(err),
    });
  }

  selectedItemsCount = computed(() => this.selectedItems().size);

  isStoreSelected(store: SellerGroup): boolean {
    return store.items.every((i) => this.selectedItems().has(i.itemId));
  }

  toggleStoreSelectAll(index: number, event: any) {
    const store = this.groupedCartItems()[index];

    const isChecked = event.target.checked;

    let updatedSet = new Set(this.selectedItems());

    store.items.forEach((item) => {
      if (isChecked) updatedSet.add(item.itemId);
      else updatedSet.delete(item.itemId);
    });

    this.selectedItems.set(updatedSet);

    const allSelectedIds = Array.from(updatedSet);

    this.cartApi.setCheckoutSelection(allSelectedIds).subscribe({
      next: (res) => this.syncSelection(res),

      error: (err) => console.error('Error syncing store selection:', err),
    });
  }

  syncSelection(snapshot: CartSnapshot) {
    this.cartSnapshot.set(snapshot);

    const ids = snapshot.sellerGroups

      .flatMap((g) => g.items)

      .filter((i) => i.isSelectedForCheckout)

      .map((i) => i.itemId);

    this.selectedItems.set(new Set(ids));
  }

  openVoucherModal(sellerId: string) {
    this.isVoucherModalOpen.update((state) => ({ ...state, [sellerId]: true }));
  }

  closeVoucherModal(sellerId: string) {
    this.isVoucherModalOpen.update((state) => ({ ...state, [sellerId]: false }));
  }

  onCouponSelected(sellerId: string, coupon: CouponType) {
    this.applyingCouponFor.set(sellerId);

    this.cartApi.setSellerCoupon(sellerId, coupon.code).subscribe({
      next: (snapshot) => {
        this.applyingCouponFor.set(null);

        this.syncSelection(snapshot);

        this.closeVoucherModal(sellerId);
      },

      error: (err) => {
        this.applyingCouponFor.set(null);

        console.error('Failed to apply coupon:', err);
      },
    });
  }

  removeCoupon(sellerId: string) {
    this.applyingCouponFor.set(sellerId);

    this.cartApi.removeSellerCoupon(sellerId).subscribe({
      next: (snapshot) => {
        this.applyingCouponFor.set(null);

        this.syncSelection(snapshot);
      },

      error: (err) => {
        this.applyingCouponFor.set(null);

        console.error('Failed to remove coupon:', err);
      },
    });
  }

  getSellerGroup(sellerId: string): SellerGroup | undefined {
    return this.groupedCartItems().find((g) => g.seller.sellerId === sellerId);
  }

  getSellerCoupons(sellerId: string): AvailableCoupon[] {
    return this.getSellerGroup(sellerId)?.availableCoupons ?? [];
  }

  hasSelectedCoupon(sellerId: string): boolean {
    const selected = this.getSellerGroup(sellerId)?.selectedCoupon;

    return !!selected;
  }

  hasAppliedCoupon(sellerId: string): boolean {
    const selected = this.getSellerGroup(sellerId)?.selectedCoupon;

    return !!selected && selected.isAvailable;
  }

  isCouponUnavailable(sellerId: string): boolean {
    const selected = this.getSellerGroup(sellerId)?.selectedCoupon;

    return !!selected && !selected.isAvailable;
  }

  getCouponUnavailableReason(sellerId: string): string {
    return this.getSellerGroup(sellerId)?.selectedCoupon?.unavailableReason ?? '';
  }

  getAppliedCouponCode(sellerId: string): string {
    return this.getSellerGroup(sellerId)?.selectedCoupon?.code ?? '';
  }
}
